// @docs:
//   - docs/core/operations/DEPARTURE_PLANNER.md
//
// Admin › Departure planner — the departure watches: a passage a boat is waiting to sail,
// re-assessed twice a day with the verdict emailed to the boat. Create, edit, pause, resume
// and end watches here, and review each draft email before it goes out. Assessments are
// made by the scheduler (vortex-api services/departurePlanner.ts) every ten minutes, or at
// once with "Assess now" (services/departureAssess.ts). Under each assessment the facts the
// email was written from: the answer, the best departure, why the others fail (in the
// engine's words) and every forecast fill, so a review checks facts, not only prose. The
// reviewer's notice links straight to Approve and Hold (routes/departureReview.ts).
// A watch's port zone follows its start unless one is chosen; "Port time zones" fixes the
// watches made before that, once.

import { useCallback, useEffect, useState } from 'react';
import { Card, Pill } from './routingUi';
import { RoutePicker, type Passage, type PickMode } from './RoutePicker';
import { RoutingBoat } from './RoutingBoat';
import { RouterTesterRequests } from './RouterTesterRequests';
import { BASEMAPS, PRESETS } from './routingMap';
import { formatLocal } from './weatherFormat';
import type { Tone } from './routingFormat';
import { TAIL_FILLS, checkText, digestFacts, isoToZonedInput, parseHours, routeText, zonedInputToIso, type TailFillId } from './departureFormat';
import {
  api,
  type DepartureAssessment,
  type DepartureWatch,
  type DepartureWatchForm,
  type DepartureWatchStatus,
  type LatLon,
  type PassageBoat,
  type PassageComfort,
  type PassageMotoring,
  type PassagePerformance,
  type PassageSailing,
  type RoutingModel,
  type RoutingPolar,
} from '../../services/api';

const PRESET = PRESETS[0];
const DEFAULT_TZ = 'Pacific/Noumea';
const fmt = (x: number) => String(x);

const MODELS: { id: RoutingModel; name: string }[] = [
  { id: 'ecmwf0p25', name: 'ECMWF' },
  { id: 'gfs025', name: 'GFS' },
  { id: 'icon025', name: 'ICON' },
];

const STATUS_TONE: Record<DepartureWatchStatus, Tone> = { active: 'ok', paused: 'warn', ended: 'mute' };
const STATE_TONE: Record<string, Tone> = {
  scanning: 'info', scanned: 'info', drafted: 'info', pending_review: 'warn', approved: 'ok', sent: 'ok', held: 'mute', failed: 'err',
};

export interface WatchFormState {
  label: string;
  vesselName: string;
  email: string;
  ownerEmail: string;
  startLat: string;
  startLon: string;
  endLat: string;
  endLon: string;
  via: LatLon[];
  departureTz: string;
  /** True: the zone above is chosen; false: the API sets it from the start. */
  departureTzManual: boolean;
  windowFrom: string; // datetime-local, on the departure port's clock
  windowDays: number;
  intervalH: number;
  models: Record<RoutingModel, boolean>;
  maxTws: string;
  maxHs: string;
  maxGust: string;
  motoring: boolean;
  motoringKt: string;
  currents: boolean;
  tailFill: TailFillId;
  /** Boat and fuel for the ratings; blank = unknown. */
  loaM: string;
  crew: string;
  fuelBurn: string;
  fuelUsable: string;
  /** Performance (contract C): % of the polar overall and at night, wave slowdown. */
  overallPct: string;
  nightPct: string;
  waveLoss: 'off' | 'light' | 'normal' | 'heavy';
  watchSystem: 2 | 3;
  experience: 'coastal' | 'offshore' | 'ocean';
  /** The settings this form does not show, kept as they were so saving never resets them. */
  kept?: {
    performance?: Partial<PassagePerformance>;
    sailing?: Partial<PassageSailing>;
    comfort?: Partial<PassageComfort>;
    motoringMore?: Partial<Pick<PassageMotoring, 'triggerKt' | 'headwindLossKtPer10' | 'reservePct' | 'maxHours'>>;
    boatMore?: Partial<Pick<PassageBoat, 'lwlM' | 'displacementKg'>>;
  };
  sendHours: string;
  requiresApproval: boolean;
}

/** A new watch: the Nouméa → Opua preset, every model, 8 am and 8 pm, approval on. */
export function watchDefaults(now = Date.now()): WatchFormState {
  const nextHour = new Date(Math.ceil(now / 3_600_000) * 3_600_000).toISOString();
  return {
    label: '', vesselName: '', email: '', ownerEmail: '',
    startLat: fmt(PRESET.start[0]), startLon: fmt(PRESET.start[1]), endLat: fmt(PRESET.end[0]), endLon: fmt(PRESET.end[1]), via: [],
    departureTz: DEFAULT_TZ, departureTzManual: false, windowFrom: isoToZonedInput(nextHour, DEFAULT_TZ), windowDays: 7, intervalH: 6,
    models: { ecmwf0p25: true, gfs025: true, icon025: true },
    maxTws: '25', maxHs: '3.5', maxGust: '', motoring: true, motoringKt: '5.5', currents: false, tailFill: 'ecmwf',
    loaM: '', crew: '', fuelBurn: '', fuelUsable: '', sendHours: '8, 20', requiresApproval: true,
    overallPct: '85', nightPct: '90', waveLoss: 'normal', watchSystem: 2, experience: 'offshore',
  };
}

function fromWatch(w: DepartureWatch): WatchFormState {
  return {
    label: w.label, vesselName: w.vesselName, email: w.email, ownerEmail: w.ownerEmail ?? '',
    startLat: fmt(w.start[0]), startLon: fmt(w.start[1]), endLat: fmt(w.end[0]), endLon: fmt(w.end[1]), via: w.via,
    departureTz: w.departureTz, departureTzManual: w.departureTzManual ?? false,
    windowFrom: isoToZonedInput(w.windowFrom, w.departureTz), windowDays: w.windowDays, intervalH: w.intervalH,
    models: { ecmwf0p25: w.models.includes('ecmwf0p25'), gfs025: w.models.includes('gfs025'), icon025: w.models.includes('icon025') },
    maxTws: fmt(w.limits.maxTwsKt), maxHs: w.limits.maxHsM == null ? '' : fmt(w.limits.maxHsM), maxGust: w.limits.maxGustKt == null ? '' : fmt(w.limits.maxGustKt),
    motoring: w.motoring.enabled, motoringKt: fmt(w.motoring.speedKt), currents: w.currents,
    // A stored 'longest' or 'climatology' runs as ECMWF (vortex-api engineTailFill), so the form shows that.
    tailFill: w.tailFill === 'strict' || w.tailFill === 'gfs' ? w.tailFill : 'ecmwf',
    loaM: w.boat?.loaM == null ? '' : fmt(w.boat.loaM), crew: w.boat?.crew == null ? '' : fmt(w.boat.crew),
    fuelBurn: w.fuel?.burnLph == null ? '' : fmt(w.fuel.burnLph), fuelUsable: w.fuel?.usableL == null ? '' : fmt(w.fuel.usableL),
    sendHours: w.sendHours.join(', '), requiresApproval: w.requiresApproval,
    overallPct: fmt(w.performance?.overallPct ?? 85), nightPct: fmt(w.performance?.nightPct ?? 90),
    waveLoss: w.performance?.waveLoss ? (w.performance.waveLoss.enabled ? w.performance.waveLoss.sensitivity : 'off') : 'normal',
    watchSystem: w.boat?.watchSystem ?? 2, experience: w.boat?.experience ?? 'offshore',
    kept: {
      performance: w.performance, sailing: w.sailing, comfort: w.comfort,
      motoringMore: w.motoring.triggerKt === undefined ? undefined : {
        triggerKt: w.motoring.triggerKt, headwindLossKtPer10: w.motoring.headwindLossKtPer10, reservePct: w.motoring.reservePct, maxHours: w.motoring.maxHours,
      },
      boatMore: w.boat ? { lwlM: w.boat.lwlM ?? null, displacementKg: w.boat.displacementKg ?? null } : undefined,
    },
  };
}

/** The form as the API takes it, or why it cannot be sent. The API checks it again. */
export function toWatchForm(f: WatchFormState, polar: RoutingPolar | null): DepartureWatchForm | string {
  const n = (s: string) => (s.trim() === '' ? NaN : Number(s));
  const opt = (s: string) => (s.trim() === '' ? null : Number(s));
  const start: LatLon = [n(f.startLat), n(f.startLon)];
  const end: LatLon = [n(f.endLat), n(f.endLon)];
  if (![...start, ...end].every(Number.isFinite)) return 'Enter the start and end as decimal degrees (south and west negative).';
  if (!f.label.trim() || !f.vesselName.trim() || !f.email.trim()) return 'Fill in the name, the vessel and the email address.';
  const models = MODELS.map((m) => m.id).filter((id) => f.models[id]);
  if (!models.length) return 'Tick at least one model.';
  const windowFrom = zonedInputToIso(f.windowFrom, f.departureTz.trim());
  if (!windowFrom) return 'Choose when the window opens, and a valid departure time zone.';
  const sendHours = parseHours(f.sendHours);
  if (!sendHours) return 'Send hours must be whole hours 0–23, separated by commas (e.g. 8, 20).';
  const [loaM, crew, burnLph, usableL] = [f.loaM, f.crew, f.fuelBurn, f.fuelUsable].map(opt);
  if (loaM !== null && !Number.isFinite(loaM)) return 'Length overall must be a number of metres, or blank.';
  if (crew !== null && !Number.isInteger(crew)) return 'Crew must be a whole number of people, or blank.';
  if ((burnLph !== null && !Number.isFinite(burnLph)) || (usableL !== null && !Number.isFinite(usableL))) return 'Fuel burn and usable fuel must be numbers, or blank.';
  const overallPct = n(f.overallPct ?? '85');
  const nightPct = n(f.nightPct ?? '90');
  if (!Number.isFinite(overallPct)) return 'Overall performance must be a % of the polar (e.g. 85).';
  if (!Number.isFinite(nightPct)) return 'Night performance must be a % (e.g. 90).';
  const k = f.kept ?? {};
  // The rest of the settings go back as they came, so this form never resets what it does not show.
  const { lightAir: _lightAir, ...keptPerf } = k.performance ?? {};
  const { maxTwsKt: _t, maxGustKt: _g, maxHsM: _h, ...keptComfort } = k.comfort ?? {};
  const boatMore = Object.fromEntries(Object.entries(k.boatMore ?? {}).filter(([, v]) => v !== null && v !== undefined));
  const watchSystem = f.watchSystem ?? 2;
  const experience = f.experience ?? 'offshore';
  const boatExtras = { ...boatMore, ...(watchSystem !== 2 ? { watchSystem } : {}), ...(experience !== 'offshore' ? { experience } : {}) };
  const waveLoss = f.waveLoss ?? 'normal';
  return {
    label: f.label.trim(), vesselName: f.vesselName.trim(), email: f.email.trim(),
    ...(f.ownerEmail.trim() ? { ownerEmail: f.ownerEmail.trim() } : {}),
    start, end, via: f.via, departureTz: f.departureTz.trim(), departureTzManual: f.departureTzManual, windowFrom, windowDays: f.windowDays, intervalH: f.intervalH,
    models, polar,
    motoring: { enabled: f.motoring, speedKt: n(f.motoringKt) || 5, ...(k.motoringMore ?? {}) },
    limits: { maxTwsKt: n(f.maxTws), maxGustKt: opt(f.maxGust), maxHsM: opt(f.maxHs) },
    performance: { ...keptPerf, overallPct, nightPct, waveLoss: waveLoss === 'off' ? { enabled: false, sensitivity: 'normal' } : { enabled: true, sensitivity: waveLoss } },
    ...(k.sailing ? { sailing: k.sailing } : {}),
    ...(Object.keys(keptComfort).length ? { comfort: keptComfort } : {}),
    currents: f.currents,
    tailFill: f.tailFill,
    boat: loaM === null && crew === null && !Object.keys(boatExtras).length ? null : { loaM, crew, ...boatExtras },
    fuel: burnLph === null && usableL === null ? null : { burnLph, usableL },
    sendHours, requiresApproval: f.requiresApproval,
  };
}

const input = 'w-full rounded-lg bg-slate-900 border border-slate-600 px-3 py-2 text-[14px] text-white focus:border-cyan-500 focus:outline-none';
const labelCls = 'block text-[13px] font-semibold text-slate-300 mb-1';
const smallButton = 'px-3 py-1.5 rounded-lg border border-slate-600 text-[13px] text-slate-200 hover:bg-slate-700 font-semibold disabled:opacity-50';

function Field({ name, children }: { name: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className={labelCls}>{name}</span>
      {children}
    </label>
  );
}

const errorText = (e: unknown) => {
  const data = (e as { response?: { data?: { error?: string; message?: string } } }).response?.data;
  return data?.error ?? data?.message ?? (e as Error).message;
};

const atPort = (iso: string | null, tz: string) => (iso ? formatLocal(Date.parse(iso), { timeZone: tz }) : null);

function Draft({ a, onDone }: { a: DepartureAssessment; onDone: () => void }) {
  const [subject, setSubject] = useState(a.emailSubject ?? '');
  const [body, setBody] = useState(a.emailBody ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const act = async (f: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await f();
      onDone();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3 mt-3">
      <Field name="Subject"><input className={input} value={subject} onChange={(e) => setSubject(e.target.value)} /></Field>
      <Field name="Email body"><textarea className={`${input} min-h-[220px] font-mono`} value={body} onChange={(e) => setBody(e.target.value)} /></Field>
      <div className="flex gap-3 flex-wrap items-center">
        <button type="button" disabled={busy} onClick={() => act(() => api.adminDepartureAssessmentApprove(a.id, { subject, body }))} className="px-5 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white text-[14px] font-semibold">
          Approve & send
        </button>
        <button type="button" disabled={busy} onClick={() => act(() => api.adminDepartureAssessmentHold(a.id))} className={smallButton}>
          Hold
        </button>
        <span className="text-[13px] text-slate-400">Approved emails go out at their send time.</span>
      </div>
      {error && <p className="text-[14px] text-red-300">{error}</p>}
    </div>
  );
}

/** The facts an email was written from: the answer, the best, why the others fail, the fills. */
function Facts({ summary, open }: { summary: unknown; open: boolean }) {
  const f = digestFacts(summary);
  if (!f) return null;
  return (
    <details open={open} className="mt-2">
      <summary className="text-[13px] font-semibold text-slate-300 cursor-pointer">The facts behind this email</summary>
      <div className="mt-2 space-y-1 text-[14px] text-slate-200">
        {f.answer && <p className="text-white">{f.answer}</p>}
        {f.best && <p><span className="text-slate-400">Best:</span> {f.best}</p>}
        {f.failures.length > 0 && (
          <ul className="space-y-1">
            {f.failures.map((x) => (
              <li key={x.when}><span className="text-slate-400">{x.when} · {x.verdict}:</span> {x.why}</li>
            ))}
          </ul>
        )}
        {f.fills.map((x) => <p key={x} className="text-slate-300">Fill · {x}</p>)}
        {f.runs && <p className="text-[13px] text-slate-400">{f.runs}</p>}
      </div>
    </details>
  );
}

function Assessments({ watch }: { watch: DepartureWatch }) {
  const [items, setItems] = useState<DepartureAssessment[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setItems(await api.adminDepartureAssessments(watch.id));
    } catch (e) {
      setError(errorText(e));
    }
  }, [watch.id]);
  useEffect(() => {
    setItems(null);
    void load();
  }, [load]);

  return (
    <Card title={`Emails — ${watch.label}`} caption={`To ${watch.email}${watch.requiresApproval ? ', after approval' : ', without approval'}. Times at the departure port.`}>
      <div className="p-5 space-y-4">
        {error && <p className="text-[14px] text-red-300">{error}</p>}
        {items && !items.length && <p className="text-[14px] text-slate-400">No assessments yet. The first runs at the next send time.</p>}
        {items?.map((a) => (
          <div key={a.id} className="rounded-lg border border-slate-700 bg-slate-900/40 px-4 py-3">
            <div className="flex items-center gap-3 flex-wrap">
              <Pill tone={STATE_TONE[a.state] ?? 'mute'}>{a.state.replace('_', ' ')}</Pill>
              <span className="text-[14px] text-white font-semibold">{a.emailSubject ?? '(no subject yet)'}</span>
              <span className="text-[13px] text-slate-400">{a.trigger === 'manual' ? 'Assess now, for ' : 'for '}{atPort(a.cycleAt, watch.departureTz)}</span>
            </div>
            {checkText(a.checkResult) && <p className={`text-[13px] mt-1 ${a.checkResult && (a.checkResult as { ok?: boolean }).ok ? 'text-slate-400' : 'text-amber-300'}`}>{checkText(a.checkResult)}</p>}
            {a.error && <p className="text-[13px] text-red-300 mt-1">{a.state === 'failed' ? `Failed: ${a.error}` : a.error}</p>}
            <Facts summary={a.summary} open={a.state === 'pending_review'} />
            {a.state === 'pending_review' && <Draft a={a} onDone={load} />}
          </div>
        ))}
      </div>
    </Card>
  );
}

export function AdminDeparturePlannerPage() {
  const [watches, setWatches] = useState<DepartureWatch[] | null>(null);
  const [f, setF] = useState<WatchFormState>(() => watchDefaults());
  const [editing, setEditing] = useState<DepartureWatch | null>(null);
  const [selected, setSelected] = useState<DepartureWatch | null>(null);
  const [polar, setPolar] = useState<RoutingPolar | null>(null);
  const [pick, setPick] = useState<PickMode>(null);
  const [basemap, setBasemap] = useState(BASEMAPS[0].id);
  const [seamarks, setSeamarks] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** Bumped by "Assess now", so the open Emails list reloads. */
  const [assessRuns, setAssessRuns] = useState(0);
  const [assessing, setAssessing] = useState<string | null>(null);
  /** The one-off port time-zone fix: what a dry run found. */
  const [zoneFixes, setZoneFixes] = useState<{ id: string; label: string; from: string; to: string; port: string }[] | null>(null);
  const set = <K extends keyof WatchFormState>(k: K, v: WatchFormState[K]) => setF((x) => ({ ...x, [k]: v }));

  const load = useCallback(async () => {
    try {
      setWatches(await api.adminDepartureWatches());
      setListError(null);
    } catch (e) {
      setListError(errorText(e));
      setWatches((w) => w ?? []);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const point = (lat: string, lon: string): LatLon | null => {
    const p: LatLon = [Number(lat), Number(lon)];
    return lat.trim() && lon.trim() && p.every(Number.isFinite) ? p : null;
  };
  const passage: Passage = { start: point(f.startLat, f.startLon), end: point(f.endLat, f.endLon), via: f.via };
  const setPassage = (p: Passage) =>
    setF((x) => ({
      ...x,
      via: p.via,
      ...(p.start ? { startLat: fmt(p.start[0]), startLon: fmt(p.start[1]) } : {}),
      ...(p.end ? { endLat: fmt(p.end[0]), endLon: fmt(p.end[1]) } : {}),
    }));

  function startEdit(w: DepartureWatch) {
    setEditing(w);
    setF(fromWatch(w));
    setPolar((w.polar as RoutingPolar | null) ?? null);
    setError(null);
  }

  function startNew() {
    setEditing(null);
    setF(watchDefaults());
    setPolar(null);
    setError(null);
  }

  async function save() {
    setError(null);
    const form = toWatchForm(f, polar);
    if (typeof form === 'string') return setError(form);
    setBusy(true);
    try {
      if (editing) await api.adminDepartureWatchUpdate(editing.id, form);
      else await api.adminDepartureWatchCreate(form);
      startNew();
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function setStatus(w: DepartureWatch, status: DepartureWatchStatus) {
    if (status === 'ended' && !window.confirm(`End the watch “${w.label}”? No more emails will be sent for it.`)) return;
    try {
      await api.adminDepartureWatchStatus(w.id, status);
      await load();
    } catch (e) {
      setListError(errorText(e));
    }
  }

  async function assessNow(w: DepartureWatch) {
    setAssessing(w.id);
    setNotice(null);
    setListError(null);
    try {
      const r = await api.adminDepartureWatchAssess(w.id);
      setNotice(r.unchanged
        ? r.message ?? 'Nothing has changed since the last assessment.'
        : `Assessing ${w.label} now. It shows under Emails; open them again to follow it.`);
      setSelected(w);
      setAssessRuns((n) => n + 1);
    } catch (e) {
      setListError(errorText(e));
    } finally {
      setAssessing(null);
    }
  }

  async function timeZones(apply: boolean) {
    setNotice(null);
    setListError(null);
    try {
      const r = await api.adminDepartureTimeZones(apply);
      setZoneFixes(apply ? null : r.fixes);
      if (apply) {
        setNotice(`Port time zones set for ${r.applied} watch${r.applied === 1 ? '' : 'es'}.`);
        await load();
      } else if (!r.fixes.length) {
        setNotice('Every watch’s port time zone already follows its start (or was chosen).');
      }
    } catch (e) {
      setListError(errorText(e));
    }
  }

  async function remove(w: DepartureWatch) {
    if (!window.confirm(`Delete the watch “${w.label}” and all its emails? This cannot be undone.`)) return;
    try {
      await api.adminDepartureWatchDelete(w.id);
      if (selected?.id === w.id) setSelected(null);
      if (editing?.id === w.id) startNew();
      await load();
    } catch (e) {
      setListError(errorText(e));
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Departure planner</h1>
        <p className="text-[14px] text-slate-400 mt-1">
          Watches re-assess a passage’s next days of departures at the send times (local at the departure port) and email the verdict to the boat. Planning output only — never a statement that a passage is safe.
        </p>
      </div>

      <Card title="Watches" caption="Newest first. Times are at each watch’s departure port.">
        {listError && <p className="px-5 pt-4 text-[14px] text-red-300">{listError}</p>}
        {notice && <p className="px-5 pt-4 text-[14px] text-slate-300">{notice}</p>}
        {watches && watches.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-[14px]">
              <thead>
                <tr className="text-left text-slate-400 text-[13px]">
                  <th className="px-5 py-2">Name</th>
                  <th className="px-5 py-2">Vessel</th>
                  <th className="px-5 py-2">Route</th>
                  <th className="px-5 py-2">Status</th>
                  <th className="px-5 py-2">Next send</th>
                  <th className="px-5 py-2">Last assessed</th>
                  <th className="px-5 py-2" />
                </tr>
              </thead>
              <tbody>
                {watches.map((w) => (
                  <tr key={w.id} className={`border-t border-slate-700 align-top ${w.id === selected?.id ? 'bg-slate-700/40' : ''}`}>
                    <td className="px-5 py-2 text-white font-semibold">{w.label}</td>
                    <td className="px-5 py-2 text-slate-200">{w.vesselName}</td>
                    <td className="px-5 py-2 text-slate-300">{routeText(w)}</td>
                    <td className="px-5 py-2">
                      <Pill tone={STATUS_TONE[w.status] ?? 'mute'}>{w.status}</Pill>
                      {w.setupLine && <p className="text-[13px] text-amber-300 mt-1">{w.setupLine}</p>}
                    </td>
                    <td className="px-5 py-2 text-slate-200 whitespace-nowrap">{atPort(w.nextSendAt, w.departureTz) ?? '—'}</td>
                    <td className="px-5 py-2 text-slate-300 whitespace-nowrap">{atPort(w.lastAssessedAt, w.departureTz) ?? 'Never'}</td>
                    <td className="px-5 py-2">
                      <div className="flex gap-2 flex-wrap">
                        <button type="button" className={smallButton} onClick={() => setSelected(w)}>Emails</button>
                        {w.status !== 'ended' && (
                          <button type="button" className={smallButton} disabled={assessing === w.id} onClick={() => assessNow(w)}>Assess now</button>
                        )}
                        <button type="button" className={smallButton} onClick={() => startEdit(w)}>Edit</button>
                        {w.status === 'active' && <button type="button" className={smallButton} onClick={() => setStatus(w, 'paused')}>Pause</button>}
                        {w.status !== 'active' && <button type="button" className={smallButton} onClick={() => setStatus(w, 'active')}>Resume</button>}
                        {w.status !== 'ended' && <button type="button" className={smallButton} onClick={() => setStatus(w, 'ended')}>End</button>}
                        <button type="button" className={`${smallButton} text-red-300`} onClick={() => remove(w)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          watches && <p className="px-5 py-4 text-[14px] text-slate-400">No watches yet. Create one below.</p>
        )}
      </Card>

      {selected && <Assessments key={`${selected.id}:${assessRuns}`} watch={selected} />}

      <RouterTesterRequests />

      <Card title="Port time zones" caption="A watch’s port time follows its start (the nearest major port) unless a zone was chosen. Watches made before that may need setting once.">
        <div className="p-5 space-y-3">
          <div className="flex gap-3 flex-wrap items-center">
            <button type="button" className={smallButton} onClick={() => timeZones(false)}>Check port time zones</button>
            {zoneFixes && zoneFixes.length > 0 && (
              <button type="button" className={smallButton} onClick={() => timeZones(true)}>
                Set {zoneFixes.length} watch{zoneFixes.length === 1 ? '' : 'es'}
              </button>
            )}
          </div>
          {zoneFixes && zoneFixes.length > 0 && (
            <ul className="text-[14px] text-slate-200 space-y-1">
              {zoneFixes.map((z) => <li key={z.id}>{z.label}: {z.from} → {z.to} (nearest port {z.port})</li>)}
            </ul>
          )}
        </div>
      </Card>

      <Card
        title={editing ? `Edit watch — ${editing.label}` : 'New watch'}
        caption="The passage, the boat, the limits, and when to email."
        action={editing ? <button type="button" className={smallButton} onClick={startNew}>Cancel</button> : undefined}
      >
        <div className="p-5 space-y-5">
          <RoutePicker passage={passage} onChange={setPassage} mode={pick} onMode={setPick} basemap={basemap} onBasemap={setBasemap} seamarks={seamarks} onSeamarks={setSeamarks} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Field name="Start latitude"><input className={input} value={f.startLat} onChange={(e) => set('startLat', e.target.value)} /></Field>
            <Field name="Start longitude"><input className={input} value={f.startLon} onChange={(e) => set('startLon', e.target.value)} /></Field>
            <Field name="End latitude"><input className={input} value={f.endLat} onChange={(e) => set('endLat', e.target.value)} /></Field>
            <Field name="End longitude"><input className={input} value={f.endLon} onChange={(e) => set('endLon', e.target.value)} /></Field>
          </div>
          <div className="flex items-center gap-3 flex-wrap text-[13px] text-slate-400">
            {PRESETS.map((pr) => (
              <button key={pr.id} type="button" className={smallButton} onClick={() => setPassage({ start: pr.start, end: pr.end, via: [] })}>
                Preset: {pr.label}
              </button>
            ))}
            <span>{PRESET.note}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field name="Name"><input className={input} placeholder="e.g. Nouméa → Opua" value={f.label} onChange={(e) => set('label', e.target.value)} /></Field>
            <Field name="Vessel"><input className={input} value={f.vesselName} onChange={(e) => set('vesselName', e.target.value)} /></Field>
            <Field name="Email to"><input type="email" className={input} placeholder="the boat’s address" value={f.email} onChange={(e) => set('email', e.target.value)} /></Field>
          </div>
          <RoutingBoat polar={polar} onPolar={setPolar} />
          <div>
            <span className={labelCls}>Boat and fuel, for the sea-state and fuel ratings (blank = unknown; ratings need both length and crew)</span>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Field name="Length overall (m)"><input className={input} value={f.loaM} onChange={(e) => set('loaM', e.target.value)} /></Field>
              <Field name="Crew"><input className={input} value={f.crew} onChange={(e) => set('crew', e.target.value)} /></Field>
              <Field name="Fuel burn (L/h)"><input className={input} value={f.fuelBurn} onChange={(e) => set('fuelBurn', e.target.value)} /></Field>
              <Field name="Usable fuel (L)"><input className={input} value={f.fuelUsable} onChange={(e) => set('fuelUsable', e.target.value)} /></Field>
            </div>
          </div>
          <div>
            <span className={labelCls}>How the boat sails on passage (the engine sails the polar at these; real boats make 75–85% of it)</span>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Field name="Overall (% of polar)"><input className={input} value={f.overallPct} onChange={(e) => set('overallPct', e.target.value)} /></Field>
              <Field name="At night (%)"><input className={input} value={f.nightPct} onChange={(e) => set('nightPct', e.target.value)} /></Field>
              <Field name="Wave slowdown">
                <select className={input} value={f.waveLoss} onChange={(e) => set('waveLoss', e.target.value as WatchFormState['waveLoss'])}>
                  <option value="off">Off</option>
                  <option value="light">On, light boat</option>
                  <option value="normal">On, normal</option>
                  <option value="heavy">On, heavy boat</option>
                </select>
              </Field>
              <Field name="Watches">
                <select className={input} value={f.watchSystem} onChange={(e) => set('watchSystem', Number(e.target.value) as 2 | 3)}>
                  <option value={2}>2 watches</option>
                  <option value={3}>3 watches</option>
                </select>
              </Field>
              <Field name="Crew experience">
                <select className={input} value={f.experience} onChange={(e) => set('experience', e.target.value as WatchFormState['experience'])}>
                  <option value="coastal">Coastal</option>
                  <option value="offshore">Offshore</option>
                  <option value="ocean">Ocean</option>
                </select>
              </Field>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Field name="Departure time zone">
                <input className={input} value={f.departureTz} disabled={!f.departureTzManual} onChange={(e) => set('departureTz', e.target.value)} />
              </Field>
              <label className="flex items-center gap-2 mt-1 text-[13px] text-slate-300">
                <input type="checkbox" checked={!f.departureTzManual} onChange={(e) => set('departureTzManual', !e.target.checked)} />
                Follow the start (nearest major port)
              </label>
            </div>
            <Field name="Window opens (port time)"><input type="datetime-local" className={input} value={f.windowFrom} onChange={(e) => set('windowFrom', e.target.value)} /></Field>
            <Field name="Days">
              <select className={input} value={f.windowDays} onChange={(e) => set('windowDays', Number(e.target.value))}>
                {Array.from({ length: 14 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d} day{d > 1 ? 's' : ''}</option>)}
              </select>
            </Field>
            <Field name="Departures a day">
              <select className={input} value={f.intervalH} onChange={(e) => set('intervalH', Number(e.target.value))}>
                {[24, 12, 8, 6, 3].map((h) => <option key={h} value={h}>{24 / h} a day (every {h} hours)</option>)}
              </select>
            </Field>
          </div>
          <div>
            <span className={labelCls}>Models</span>
            <div className="flex gap-4 flex-wrap">
              {MODELS.map((m) => (
                <label key={m.id} className="flex items-center gap-2 text-[14px] text-slate-200">
                  <input type="checkbox" checked={f.models[m.id]} onChange={(e) => set('models', { ...f.models, [m.id]: e.target.checked })} />
                  {m.name}
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Field name="Maximum wind (kn)"><input className={input} value={f.maxTws} onChange={(e) => set('maxTws', e.target.value)} /></Field>
            <Field name="Maximum waves (m, blank = none)"><input className={input} value={f.maxHs} onChange={(e) => set('maxHs', e.target.value)} /></Field>
            <Field name="Maximum gust (kn, blank = none)"><input className={input} value={f.maxGust} onChange={(e) => set('maxGust', e.target.value)} /></Field>
            <Field name="Motor at (kn)"><input className={input} value={f.motoringKt} disabled={!f.motoring} onChange={(e) => set('motoringKt', e.target.value)} /></Field>
          </div>
          <div>
            <span className={labelCls}>When a forecast ends before you arrive</span>
            <div className="space-y-2">
              {TAIL_FILLS.map((t) => (
                <label key={t.id} className="flex items-start gap-2 text-[14px] text-slate-200">
                  <input type="radio" name="tailFill" className="mt-1" checked={f.tailFill === t.id} onChange={() => set('tailFill', t.id)} />
                  <span><span className="font-semibold text-white">{t.title}.</span> {t.sentence}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field name="Send at (local hours at the port)"><input className={input} value={f.sendHours} onChange={(e) => set('sendHours', e.target.value)} /></Field>
            <Field name="Owner (account email)"><input type="email" className={input} placeholder="blank = you" value={f.ownerEmail} onChange={(e) => set('ownerEmail', e.target.value)} /></Field>
          </div>
          <div className="flex gap-6 flex-wrap text-[14px] text-slate-200">
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.motoring} onChange={(e) => set('motoring', e.target.checked)} /> Motor when the wind is too light</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.currents} onChange={(e) => set('currents', e.target.checked)} /> Use forecast currents</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.requiresApproval} onChange={(e) => set('requiresApproval', e.target.checked)} /> Needs approval before sending</label>
          </div>
          <div className="flex gap-4 items-center flex-wrap">
            <button type="button" onClick={save} disabled={busy} className="px-6 py-2.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white text-[15px] font-semibold">
              {busy ? 'Saving…' : editing ? 'Save changes' : 'Create watch'}
            </button>
            {error && <p className="text-[14px] text-red-300">{error}</p>}
          </div>
        </div>
      </Card>
    </div>
  );
}
